// scheduling/hooks/useGenTrackBands.js
// Shared loader for the Gen Track tax/assurance bands behind the "Needs more Tax /
// Needs more Assurance" badge (Trello #309).
//
// WHY A MODULE-LEVEL CACHE (the only one in scheduling/ — deliberate)
// -------------------------------------------------------------------
// Six surfaces render staff candidates and all six want the same answer: the
// Suggestions page, the placeholder-assign modal, the schedule chooser (suggestions
// AND its manual add-staff search), both grid picklists, and conflict resolution.
// The data comes from schedData `gentrack_bands`, which aggregates pe.TranWIP -- 6.8M
// rows with ONLY a clustered PK on WIPIndex, so every call is a full clustered scan
// (~600ms warm, ~4.6s cold). Six independent effects would mean six scans, several of
// them concurrent, competing for the SWA's shared worker pool.
//
// So the fetch is hoisted out of React entirely: one in-flight promise shared by every
// caller, and the resolved value kept for the life of the page. Nothing invalidates it
// mid-session, which is correct rather than lazy -- the default basis is ALL-TIME
// charged hours plus what is already scheduled, so a person's band does not
// meaningfully move while someone is working in the app. A reload refetches.
//
// Never rejects. The badge is advisory: if this fails, every surface renders exactly as
// it did before the badge existed, silently. That is why callers get an empty map
// rather than an error to handle.

import { useEffect, useMemo, useState } from 'react';
import { fetchGenTrackBands } from '../services/schedData';

// Module-level, NOT per-hook state: the whole point is that mounting a second consumer
// does not trigger a second scan.
let cachedPromise = null;

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Load (once per page) and return the Gen Track band payload.
 *
 * `today` is the CALLER's local date, not the server's: the Function runs UTC, so after
 * ~7pm Central its "today" is already tomorrow, and this payload's forward side is
 * "scheduled from today on". Same reason fetchGenTrackBalance ships one.
 */
export function loadGenTrackBands() {
  if (!cachedPromise) {
    cachedPromise = fetchGenTrackBands(todayISO()).catch(() => null);
  }
  return cachedPromise;
}

/** Test/dev escape hatch — drops the cache so the next consumer refetches. */
export function resetGenTrackBandsCache() {
  cachedPromise = null;
}

/**
 * @returns {{
 *   bandByStaff: Object,   // StaffId -> { staffId, taxPct, totalHours }
 *   lists: object|null,    // { taxCodes, assuranceCodes, taxDescs, assuranceDescs } for jobBucketOf
 *   loading: boolean,
 * }}
 *
 * `lists` comes straight from the server response and is passed to jobBucketOf rather
 * than being duplicated client-side -- that echo is the whole reason gentrack_bands
 * returns the service code/name lists at all. A second copy of DJB's service lists in
 * client code would eventually disagree with the aggregation that uses them.
 */
export function useGenTrackBands() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadGenTrackBands().then((r) => {
      if (cancelled) return;
      setData(r);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // StaffId -> band, so each surface can look a candidate up by id without scanning.
  const bandByStaff = useMemo(() => {
    const m = {};
    (data?.bands || []).forEach((b) => { m[b.staffId] = b; });
    return m;
  }, [data]);

  return { bandByStaff, lists: data, loading };
}

export default useGenTrackBands;
